import { useEffect, useMemo, useRef, useState } from 'react';
import { InlineNotification, Tag } from '@carbon/react';
import { Tearsheet } from '@carbon/ibm-products';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import {
  useBuildWellbore,
  useLtdCandidates,
  type WellboreTarget,
} from '../../hooks';
import type { WellboreBuilt } from '../../api';
import {
  END_A,
  type LtdCandidate,
  type LtdEndKey,
  type LtdEndRef,
  REFUSAL_MESSAGES,
  defaultBottomEnd,
  designationRefusal,
  isBottomEndValid,
  otherEnd,
  preferredEndRef,
  toSurfaceBody,
  toWellboreBody,
} from './composite-geometry';
import {
  useBottomHoleOptions,
  useRefreshLtdCandidates,
  useWellborePreview,
} from '../../hooks';
import type { LtdFacet } from './ltd-candidates';
import { LtdFilterPanel } from './ltd-filter-panel';
import { LtdSelectionTable } from './ltd-selection-table';
import { LtdSelectionMap } from './ltd-selection-map';
import { WellboreEndDesignation } from './wellbore-end-designation';
import styles from './add-wellbore-tearsheet.module.scss';

/** Refusals the SHEET cannot act on — the well or the bore is wrong, not the selection. */
const HOST_OWNED_REFUSALS = new Set([
  'BORE_NOT_FOUND',
  'WORKSPACE_PROMOTED',
  'NOT_A_NEW_WELL',
]);

/** The refusal that says the LISTED sticks are out of date, not that the selection is wrong. */
const STALE_SLOTS = 'INVALID_LTD_SLOTS';

interface AddWellboreTearsheetProps {
  open: boolean;
  workspaceId: number;
  /** The authored well the bore is being built for. */
  stagedWellId: number | null;
  uwi: string;
  wellName: string;
  /** Whether this adds a bore or rebuilds one that already exists. */
  target: WellboreTarget;
  onClose: () => void;
  /** The bore landed; the host re-reads its cards. */
  onBuilt: (result: WellboreBuilt) => void;
  /**
   * A refusal about the WELL or the BORE rather than the selection — the sheet closes and
   * the host says it, because nothing in here could fix it and leaving the sheet open
   * invites a retry that cannot succeed.
   */
  onFailed?: (message: string) => void;
}

const EMPTY_FACETS: Partial<Record<LtdFacet, string[]>> = {};

/** What `resolveBottomEnd` needs to settle the bottom hole. */
interface BottomEndInput {
  /** The sticks currently checked. */
  selected: LtdCandidate[];
  /** Their ids, for membership tests. */
  selectedIds: Set<number>;
  /** The settled surface hole. */
  surface: LtdEndRef | null;
  /** What the reader designated, if anything. */
  bottomPick: LtdEndRef | null;
  /** Valid bottom end per stick, once the backend has answered. */
  validBottomEnds: Map<number, LtdEndKey> | null;
}

/**
 * The bottom hole: the reader's pick where it still stands, else the composite's default.
 * A pure derivation, kept out of the sheet so the sheet reads as wiring.
 * @param {BottomEndInput} input The selection, the surface hole and the pick.
 * @return {LtdEndRef | null} The bottom hole, or null while there cannot be one.
 */
const resolveBottomEnd = ({
  selected,
  selectedIds,
  surface,
  bottomPick,
  validBottomEnds,
}: BottomEndInput): LtdEndRef | null => {
  if (!surface || selected.length === 0) return null;

  // One lateral: the bottom hole can only be its other end, so there is nothing to pick
  // and nothing to default — it follows the surface.
  if (selected.length === 1) {
    const only = selected[0];
    const opposite = otherEnd(surface.end);
    return (only.ends ?? []).some((end) => end.id === opposite)
      ? { slotId: only.ltdPropertyId, end: opposite }
      : null;
  }

  if (
    bottomPick &&
    bottomPick.slotId !== surface.slotId &&
    selectedIds.has(bottomPick.slotId) &&
    // Invalid picks are cleared by the effect above; this keeps the render in between
    // from ever carrying one.
    isBottomEndValid(validBottomEnds, bottomPick)
  ) {
    return bottomPick;
  }

  /* The composite's default far end. The mock picks "wherever the chain runs out", which
     it knows because it derives the chain itself; this sheet does not — the chaining is
     the backend's (see composite-geometry). The LAST-CHECKED non-surface stick is the
     honest stand-in, on the end the backend names as valid once it has answered. The
     backend's own `orderedMembers` then reports the real traversal order in the summary.
     Re-designate to change it. */
  return defaultBottomEnd(selected, surface, validBottomEnds);
};

/** What the Save button says, given what the sheet is doing. */
interface SaveLabelInput {
  /** Whether the build request is out. */
  saving: boolean;
  /** Whether this sheet is replacing an existing bore rather than adding one. */
  rebuilding: boolean;
  /** How many LTDs are checked. */
  count: number;
}

/**
 * The Save button's label. Out here because it is four phrasings of one sentence, and
 * inline it reads as four decisions in the middle of the action bar.
 * @param {SaveLabelInput} input What the sheet is doing.
 * @return {string} The label.
 */
const saveLabel = ({ saving, rebuilding, count }: SaveLabelInput): string => {
  const verb = rebuilding ? 'Rebuild' : 'Add';
  if (saving) return `${rebuilding ? 'Rebuilding' : 'Adding'}…`;

  return count > 1
    ? `${verb} composite wellbore (${count} LTDs)`
    : `${verb} wellbore`;
};

/** The chips for checked sticks the table cannot currently show. */
interface OffPageSelectionsProps {
  /** The checked sticks that are not on the current page. */
  candidates: LtdCandidate[];
  /** True while a build is out, which freezes the chips. */
  disabled: boolean;
  /** Takes one stick out of the composite. */
  onRemove: (ltdPropertyId: number) => void;
}

/**
 * Selections the table cannot reach.
 *
 * ONLY the checked sticks that are not on the current page. The table's checkboxes cover the
 * rows it is showing, and this picker is server-paged, so a stick the reader has filtered or
 * paged away from has no checkbox and would otherwise sit in the composite with no way to take
 * it out. (The mock has no equivalent because it holds every LTD client-side and never loses a
 * row.) Renders nothing when the selection is fully visible, which is the ordinary case.
 *
 * A stick the BACKEND dropped as stale is not handled here: it is pruned by id from
 * `details.staleSlotIds`, before the reader has to notice it at all. Removal routes through the
 * sheet's own toggle, so an explicit designation on the dropped stick clears exactly as
 * unchecking its row would.
 * @param {OffPageSelectionsProps} props The unreachable selections and the remove handler.
 * @return {JSX.Element | null} The chips, or nothing when everything is on screen.
 */
function OffPageSelections({
  candidates,
  disabled,
  onRemove,
}: OffPageSelectionsProps) {
  if (candidates.length === 0) return null;

  return (
    <div className={styles.selectionList}>
      <span className={styles.selectionNote}>
        {candidates.length === 1
          ? '1 selected LTD is not shown by the current filters'
          : `${candidates.length} selected LTDs are not shown by the current filters`}
      </span>
      {candidates.map((candidate) => {
        const label = candidate.name ?? `LTD ${candidate.ltdPropertyId}`;

        return (
          <Tag
            key={candidate.ltdPropertyId}
            type="blue"
            size="md"
            filter
            disabled={disabled}
            // Names the stick rather than the action — "Remove from this
            // wellbore" on each of several chips reads as N identical buttons
            // to anyone not looking at the chip beside it.
            title={`Remove ${label}`}
            onClose={() => onRemove(candidate.ltdPropertyId)}
          >
            {label}
          </Tag>
        );
      })}
    </div>
  );
}

/** The banner shown when the build itself was refused. */
interface BuildFailureProps {
  /** What the backend said, or null when the last attempt stands. */
  message: string | null;
  /** Whether the sheet is replacing a bore rather than adding one. */
  rebuilding: boolean;
  /** Clears the refusal. */
  onDismiss: () => void;
}

/**
 * The build refusal, worded for whichever action was attempted.
 * @param {BuildFailureProps} props The message, what was attempted, and the dismiss.
 * @return {JSX.Element | null} The banner, or nothing when there is no refusal.
 */
function BuildFailure({ message, rebuilding, onDismiss }: BuildFailureProps) {
  if (!message) return null;

  return (
    <InlineNotification
      kind="error"
      lowContrast
      className={styles.banner}
      title={
        rebuilding
          ? 'Could not rebuild the wellbore'
          : 'Could not add the wellbore'
      }
      subtitle={message}
      onCloseButtonClick={onDismiss}
    />
  );
}

/**
 * What the current selection means, in one banner: a single lateral is taken as the shape as
 * is, several are chained into a composite.
 * @param {{count: number}} props How many sticks are checked.
 * @return {JSX.Element | null} The banner, or nothing before anything is checked.
 */
function SelectionGuidance({ count }: { count: number }) {
  if (count === 0) return null;
  const single = count === 1;

  return (
    <InlineNotification
      kind="info"
      lowContrast
      hideCloseButton
      className={styles.banner}
      title={
        single
          ? 'One LTD selected — its lateral becomes the wellbore shape as is.'
          : `Combining ${count} LTD sticks into one wellbore shape.`
      }
      subtitle={
        single
          ? 'Check neighbouring LTDs to chain them into a composite. Set the surface hole and bottom hole on the map, or in the lists under it.'
          : 'The laterals are chained end-to-end by derived connections into a single trajectory. Set its surface hole and bottom hole on the map, or in the lists under it.'
      }
    />
  );
}

/**
 * Build a wellbore for a well authored in a workspace — the ADD and the REBUILD, one sheet.
 *
 * A created well has no trajectory, so State, County, SIM Landing Zone and the perforated
 * lateral have nothing to be computed from and it cannot be promoted. The first bore is
 * where that changes: one or more LTD sticks are picked, a surface hole and a bottom hole
 * designated, and the backend builds the (composite) bore from those inputs. A well may
 * then hold several bores, and `target` says whether this save adds another or replaces an
 * existing one's geometry in place.
 *
 * ONE sheet rather than two, because only the title, the primary label, one banner and the
 * HTTP verb differ — the picker, the facets, the live preview, the designation reducer and
 * the map are the same question either way. The alternative is a second copy of the
 * designation logic, which is the drift this feature already paid once to retire.
 *
 * A REBUILD STARTS EMPTY, and says so. Nothing persists which sticks a bore was built from
 * (the builder writes geometry and points; the slot ids are dropped), so there is nothing
 * to seed the picker with. The sheet is explicit about that rather than letting the word
 * "edit" imply the previous picks are remembered somewhere.
 *
 * The `@carbon/ibm-products` Tearsheet, as the mock draws it. Its own wide-width rule does
 * not win against the base modal-container width in this app's `bx--` Carbon build, so the
 * width is restored in the stylesheet — the same override the eval3 asset picker and the
 * Wellbore Geometry tearsheet already carry.
 * @param {AddWellboreTearsheetProps} props The well, the workspace and the exits.
 * @return {JSX.Element} The sheet.
 */
// eslint-disable-next-line max-lines-per-function -- one sheet wiring the filter rail, the LTD table, the map and the designations to a single selection; the parts share that selection, so splitting them would turn local state into prop plumbing.
export function AddWellboreTearsheet({
  open,
  workspaceId,
  stagedWellId,
  uwi,
  wellName,
  target,
  onClose,
  onBuilt,
  onFailed,
}: AddWellboreTearsheetProps) {
  const [facets, setFacets] =
    useState<Partial<Record<LtdFacet, string[]>>>(EMPTY_FACETS);
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /** Sticks checked across every page — the composite's membership. */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  /** Sticks kept by id, so a selection survives paging away from its row. */
  const [selectedById, setSelectedById] = useState<Map<number, LtdCandidate>>(
    new Map()
  );
  /**
   * The reader's EXPLICIT designations. Null means "not chosen yet" — the effective
   * surface/bottom below fill the gap so the trajectory is whole from the first check,
   * and step aside once the reader has spoken.
   */
  const [surfacePick, setSurfacePick] = useState<LtdEndRef | null>(null);
  const [bottomPick, setBottomPick] = useState<LtdEndRef | null>(null);
  const [pickRole, setPickRole] = useState<'surface' | 'bottom' | null>(null);
  /** The reader's bottom pick was cleared because the chain no longer accepts it. */
  const [bottomReset, setBottomReset] = useState(false);

  const rebuilding = target.mode === 'rebuild';
  /* Keys the reset below. Re-opening on a DIFFERENT bore without an intervening unmount
     must blank the picker, or the sheet would resume the last bore's selection. */
  const targetKey = rebuilding ? target.completionUwi14 : 'create';
  const builder = useBuildWellbore(workspaceId, stagedWellId, uwi, target);

  // Every open starts clean, so a sheet reopened after a save does not resume the last one.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reopening the sheet resets it; `open` is a prop, and keying the whole sheet on it would remount the map and the LTD table too.
    setFacets(EMPTY_FACETS);
    setText('');
    setPage(1);
    setSelectedIds(new Set());
    setSelectedById(new Map());
    setSurfacePick(null);
    setBottomPick(null);
    setBottomReset(false);
    setPickRole(null);
    builder.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `builder` is rebuilt every render by useMutation, so listing it would reset the sheet on every render.
  }, [open, targetKey]);

  const query = useMemo(
    () => ({ facets, text, page, pageSize }),
    [facets, text, page, pageSize]
  );
  const { data, isLoading, isFetching, isPlaceholderData, error } =
    useLtdCandidates(query, open);
  /* The LTD filter rail spins while its option lists are in flight. `isLoading` is false
     during a refetch (`keepPreviousData`), so it would only ever fire on the first open;
     but bare `isFetching` over-fires, because the query key carries `page`/`pageSize` too
     and the backend recomputes the same facet lists whatever page is asked for. Spin only
     while the request in flight is for a different FILTER combination than the one on
     screen. */
  const filterKey = JSON.stringify([facets, text]);
  const shownFilterKey = useRef(filterKey);
  // eslint-disable-next-line react-hooks/refs -- remembers which filter the SHOWN rows belong to, so the facet spinner appears only for the filter still in flight; an effect would flash it a frame late.
  if (!isFetching) shownFilterKey.current = filterKey;
  // eslint-disable-next-line react-hooks/refs -- reads the key written on the line above, for the same spinner test.
  const facetsLoading = isFetching && shownFilterKey.current !== filterKey;

  const candidates = useMemo(() => data?.results ?? [], [data]);
  /* `keepPreviousData` holds the last page's rows while a new filter loads, which suits the
     table but not the map: it would keep drawing the PREVIOUS filter's sticks as if they
     were the current set. The map shows only the set that answers the current query. */
  const mapCandidates = useMemo(
    () => (isPlaceholderData ? [] : candidates),
    [isPlaceholderData, candidates]
  );
  /* What the map CENTERS on is a separate question from what it draws. Blanking the drawn
     sticks while stale must not also blank the framing, or the map jumps to its default box
     on every filter change and back when the data lands. So framing holds the last settled,
     non-empty set (dropped on close so a reopen does not frame the previous session). */
  const settledRef = useRef<typeof candidates>([]);
  const framingCandidates = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs -- holds the last SETTLED page so the map keeps its framing while the next page loads; placeholder rows must not re-frame it.
    if (!open) settledRef.current = [];
    else if (!isPlaceholderData && candidates.length > 0)
      // eslint-disable-next-line react-hooks/refs -- as above: only a settled, non-empty page replaces the framing set.
      settledRef.current = candidates;
    // eslint-disable-next-line react-hooks/refs -- as above: returns the held set.
    return settledRef.current;
  }, [open, isPlaceholderData, candidates]);

  // Remember the full row for anything checked, so the designation dropdowns and the map
  // still know a stick's ends after the reader pages away from it.
  useEffect(() => {
    if (candidates.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- remembers the full row for anything checked as pages arrive, so the designation dropdowns keep a stick that has paged out of view.
    setSelectedById((prev) => {
      const next = new Map(prev);
      candidates.forEach((candidate) => {
        if (selectedIds.has(candidate.ltdPropertyId)) {
          next.set(candidate.ltdPropertyId, candidate);
        }
      });
      return next;
    });
  }, [candidates, selectedIds]);

  const selected = useMemo(
    () =>
      [...selectedIds]
        .map((id) => selectedById.get(id))
        .filter(
          (candidate): candidate is LtdCandidate => candidate !== undefined
        ),
    [selectedIds, selectedById]
  );

  const selectedSlotIds = useMemo(
    () => selected.map((candidate) => candidate.ltdPropertyId),
    [selected]
  );

  /**
   * Checked sticks with no row on the current page.
   *
   * The only ones that need a dismiss of their own: every other selection already has a
   * checkbox a click away. Derived from what the table is actually showing rather than
   * from the page number, so it covers filtering and searching too, not just paging.
   */
  const offPageSelected = useMemo(
    () =>
      selected.filter(
        (candidate) =>
          !candidates.some(
            (row) => row.ltdPropertyId === candidate.ltdPropertyId
          )
      ),
    [selected, candidates]
  );

  /* ── Effective designations ──
     The bore must be whole from the FIRST check rather than after two dropdown trips, so an
     unset designation — or one invalidated by its stick being unchecked — falls back to a
     derived default instead of leaving the trajectory broken. A default is only ever a
     starting point: an explicit pick on a still-selected stick always wins. */
  const surface = useMemo<LtdEndRef | null>(() => {
    if (selected.length === 0) return null;
    if (surfacePick && selectedIds.has(surfacePick.slotId)) return surfacePick;
    return preferredEndRef(selected[0], END_A);
  }, [selected, selectedIds, surfacePick]);

  /* Which end of each stick the chain accepts as the bottom hole, for the current surface
     hole + selection. Keyed on the surface alone, so picking a bottom does not refetch it.
     Null while unknown, which flags nothing. */
  const { validEnds: validBottomEnds, resolved: optionsResolved } =
    useBottomHoleOptions(
      open && surface && selectedSlotIds.length > 0
        ? toSurfaceBody(selectedSlotIds, surface)
        : null
    );

  /* The composite's default bottom hole is a function of the options, so until they settle
     it is a GUESS — and a guess the chain rejects shows as a 422 banner, then re-fires the
     preview once the options land and the bottom moves. The preview body is therefore held
     back while the options are in flight (a failed request ends the wait, falling back to
     the unflagged default). One lateral needs no options: its bottom is the far end. */
  const awaitingOptions =
    surface !== null && selected.length > 1 && !optionsResolved;

  /* A pick the chain has since made unreachable (the selection or surface moved) is CLEARED,
     not merely overridden: left set, it would silently come back if validity flipped again.
     The notice says so, since the field changes under the reader. */
  const pickInvalid =
    selected.length > 1 &&
    bottomPick !== null &&
    !isBottomEndValid(validBottomEnds, bottomPick);
  useEffect(() => {
    if (!pickInvalid) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the bottom pick stopped being valid for the current surface hole, so it clears; validity comes from the backend's dry run.
    setBottomPick(null);
    setBottomReset(true);
  }, [pickInvalid]);

  const bottom = useMemo<LtdEndRef | null>(
    () =>
      resolveBottomEnd({
        selected,
        selectedIds,
        surface,
        bottomPick,
        validBottomEnds,
      }),
    [selected, selectedIds, surface, bottomPick, validBottomEnds]
  );

  // Cheap INPUT validation only — is there anything worth asking the backend about. Mirrors
  // validate_designations! so the button and its copy are instant rather than trailing the
  // debounce; whether the sticks actually chain is the server's answer.
  const inputRefusal = designationRefusal(selectedSlotIds, surface, bottom);

  /**
   * ONE body, used for the preview and for the save.
   *
   * This is what guarantees the sheet draws what the save builds: the two requests are the
   * same object through the same endpoint params. Two builders would reintroduce the drift
   * that retiring the client-side chain model removed.
   */
  const body = useMemo(() => {
    if (inputRefusal !== null || !surface || !bottom) return null;
    // Neither previewed nor saveable until the bottom hole is the settled one.
    if (awaitingOptions) return null;
    return toWellboreBody(selectedSlotIds, surface, bottom);
  }, [inputRefusal, selectedSlotIds, surface, bottom, awaitingOptions]);

  const preview = useWellborePreview(open ? body : null);

  /**
   * Adds or removes sticks from the selection.
   * @param {number[]} ids The sticks the table just toggled.
   * @param {boolean} isSelected Whether they are now selected.
   */
  const handleToggle = (ids: number[], isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (isSelected ? next.add(id) : next.delete(id)));
      return next;
    });

    // An EXPLICIT designation on a stick that has just been unchecked is no longer
    // meaningful — leaving it would derive a chain from a stick the reader can no longer
    // see. Clearing the pick hands the role back to the derived default.
    if (!isSelected) {
      const dropped = new Set(ids);
      if (surfacePick && dropped.has(surfacePick.slotId)) setSurfacePick(null);
      if (bottomPick && dropped.has(bottomPick.slotId)) setBottomPick(null);
    }
  };

  /**
   * A designation, from either dropdown or from a map click.
   *
   * The two roles cannot be held independently, so each pick reconciles the other:
   * - On ONE lateral there is only the question of which way it is drilled, so designating
   *   a bottom hole is really moving the SURFACE to the opposite end.
   * - On a composite the two roles cannot share a stick — the chain could never pass
   *   through the members between them — so a collision displaces the other role rather
   *   than being refused.
   * @param {'surface' | 'bottom'} role Which hole is being set.
   * @param {LtdEndRef | null} ref The endpoint, or null to hand the role back to its default.
   * @return {void}
   */
  const handleDesignate = (
    role: 'surface' | 'bottom',
    ref: LtdEndRef | null
  ) => {
    if (ref === null) {
      if (role === 'surface') setSurfacePick(null);
      else setBottomPick(null);
      return;
    }

    if (role === 'bottom') setBottomReset(false);

    if (role === 'surface') {
      setSurfacePick(ref);
      if (selected.length > 1 && bottomPick?.slotId === ref.slotId) {
        setBottomPick(null);
      }
      return;
    }

    if (selected.length === 1) {
      setSurfacePick({ slotId: ref.slotId, end: otherEnd(ref.end) });
      setBottomPick(null);
      return;
    }

    setBottomPick(ref);
    if (surface?.slotId === ref.slotId) {
      const other = selected.find(
        (candidate) => candidate.ltdPropertyId !== ref.slotId
      );
      if (other) setSurfacePick(preferredEndRef(other, END_A));
    }
  };

  /** Builds the bore from the current designations, then hands it back to the page. */
  const handleSave = () => {
    if (!body) return;
    builder.build(body, (built) => {
      onBuilt(built);
      onClose();
    });
  };

  /* A refusal about the WELL or the BORE is not something this sheet can help with, so it
     closes and hands the message to the card. Everything else is about the selection,
     which is on screen, so the sheet stays open with it.

     Routed on the CODE, never on the status: the provider runs before the bore is
     resolved, so a rebuild of a bore that no longer exists answers 422 INVALID_LTD_SLOTS
     rather than 404 if the selection also went stale. A 422 is not proof the bore is
     still there. */
  useEffect(() => {
    if (!builder.errorCode || !HOST_OWNED_REFUSALS.has(builder.errorCode))
      return;
    onFailed?.(builder.error ?? 'This wellbore can no longer be built.');
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `builder` and the callbacks are rebuilt every render; the error code is the only real trigger.
  }, [builder.errorCode]);

  /* A stale-stick refusal says the LIST is out of date, so it is re-read — from whichever
     request hit it first, which is almost always the preview: it runs on every selection
     change, while the save needs the reader to reach for it.

     The PREFIX is the only sane target. The whole query object rides the key, so every
     facet/text/page combination is its own entry and there is no single one to name.
     Refetching is cheap and invisible here: `keepPreviousData` holds the rows on screen
     while it happens, and the paging lives in this component's state, so nothing resets
     under the reader.

     This does NOT prune the selection, and must not — the refreshed list is a page through
     a filter, so a stick missing from it may simply be on page two. Dropping a stick is the
     reader's to do, which is what the selection list below is for. */
  const refreshLtdCandidates = useRefreshLtdCandidates();
  const slotsWentStale =
    builder.errorCode === STALE_SLOTS || preview.errorCode === STALE_SLOTS;
  useEffect(() => {
    if (!slotsWentStale) return;
    refreshLtdCandidates();
  }, [slotsWentStale, refreshLtdCandidates]);

  /* The backend NAMES the stale slots, so they are dropped precisely rather than left for
     the reader to find. This is what makes pruning safe at all: intersecting the selection
     with the refreshed list would also drop a stick that is merely on another page of the
     current filter, which is why it was not done before the ids existed.

     Keyed on the ids themselves, so a second refusal naming different sticks prunes again.
     Routed through `handleToggle`, so an explicit designation on a dropped stick clears
     exactly as unchecking its row would and the derived ends re-resolve from what is left.
     The removable list stays: a stick can be wrong without being stale. */
  const staleIds = builder.staleSlotIds ?? preview.staleSlotIds ?? null;
  const staleKey = staleIds ? staleIds.join(',') : '';
  useEffect(() => {
    if (!staleIds || staleIds.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the backend named sticks that went stale, so they are dropped from the selection.
    handleToggle(staleIds, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `handleToggle` is rebuilt every render; the named ids are the trigger.
  }, [staleKey]);

  // What the backend says it traversed — not what the reader checked.
  const count = preview.orderedMembers.length;

  return (
    <Tearsheet
      open={open}
      onClose={onClose}
      className={styles.tearsheet}
      label={wellName}
      title={rebuilding ? 'Rebuild Wellbore' : 'Add Wellbore'}
      description={rebuilding ? `Completion ${targetKey}` : undefined}
      hasCloseIcon
      closeIconDescription="Close"
      // The sheet's own footer — the Tearsheet renders these right-aligned in its action
      // bar, so the sheet no longer carries a hand-built one.
      actions={[
        {
          // Ghost rather than Carbon's dark `secondary`: the mock's dismiss reads as a
          // text action beside the blue primary, not a second filled button.
          key: 'cancel',
          kind: 'ghost',
          label: 'Cancel',
          disabled: builder.saving,
          onClick: onClose,
        },
        {
          key: 'save',
          kind: 'primary',
          label: saveLabel({ saving: builder.saving, rebuilding, count }),
          disabled: !body || preview.loading || count === 0 || builder.saving,
          onClick: handleSave,
        },
      ]}
    >
      {/* Body only while open. Building a mapbox instance (and its GL context) behind a
          closed sheet costs one per workspace page load for nothing. */}
      {open && (
        <div className={styles.body}>
          <div className={styles.banners}>
            {/* What the sheet is doing with the checked laterals, stated from the FIRST
                check. One lateral is the plain case and reads differently from a composite.
                Counted off the CHECKED sticks rather than the backend's traversal, so the
                banner answers the selection immediately instead of trailing the preview. */}
            {/* Said up front, every time, before anything is picked: a rebuild REPLACES,
                and it starts empty because nothing recorded what the bore was made of.
                Not dismissible — it is the one thing a reader has to know here, and it
                stops "Rebuild" being read as "Edit", which would imply the previous picks
                are remembered somewhere. */}
            {rebuilding && (
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                className={styles.banner}
                title="Rebuilding replaces this wellbore's geometry."
                subtitle="The LTDs it was originally built from are not recorded, so this starts from an empty selection. Saving replaces the existing trajectory and all of its points. Cancel to leave it unchanged."
              />
            )}

            <SelectionGuidance count={selected.length} />

            {/* The backend's advisories — an over-long derived tie, a stick carrying no
                perforated length. They sit WITH the info banner, at the top, because they are
                about the selection and this is where the selection can still be changed.
                Advisory only: the bore still builds, so they never hold the action.

                ONE notification carrying all of them, not one each. The backend emits a
                separate warning per stick and per tie, so a three-stick composite can raise
                four — stacked, they push the table and the map off the sheet, which is the
                opposite of letting the reader fix the selection. */}
            {preview.warnings.length > 0 && (
              <InlineNotification
                kind="warning"
                lowContrast
                hideCloseButton
                className={styles.banner}
                title="Check this selection"
                subtitle={preview.warnings.join('. ')}
              />
            )}
          </div>

          <BuildFailure
            message={builder.error}
            rebuilding={rebuilding}
            onDismiss={() => builder.reset()}
          />

          {/* ── Selections the table cannot reach ──
              ONLY the checked sticks that are not on the current page. The table's
              checkboxes cover the rows it is showing, and this picker is server-paged, so
              a stick the reader has filtered or paged away from has no checkbox and would
              otherwise sit in the composite with no way to take it out. (The mock has no
              equivalent because it holds every LTD client-side and never loses a row.)

              Hidden whenever the selection is fully visible, which is the ordinary case —
              so the sheet reads like the mock until something is actually out of reach.
              A stick the BACKEND dropped as stale is not handled here: it is pruned by id
              from `details.staleSlotIds`, before the reader has to notice it at all.

              Removal routes through `handleToggle`, so an explicit designation on the
              dropped stick clears exactly as unchecking its row would, and the derived
              surface/bottom re-resolve from what is left. */}
          <OffPageSelections
            candidates={offPageSelected}
            disabled={builder.saving}
            onRemove={(id) => handleToggle([id], false)}
          />

          <div className={styles.columns}>
            <LtdFilterPanel
              available={data?.facets ?? {}}
              selected={facets}
              disabled={builder.saving}
              loading={facetsLoading}
              onChange={(facet, values) => {
                setFacets((prev) => ({ ...prev, [facet]: values }));
                setPage(1);
              }}
              onReset={() => {
                setFacets(EMPTY_FACETS);
                setPage(1);
              }}
            />

            <div className={styles.tablePane}>
              <LtdSelectionTable
                candidates={candidates}
                totalCount={data?.total ?? 0}
                loading={isLoading}
                error={error ? 'Could not load LTD properties.' : null}
                selectedIds={selectedIds}
                onToggle={handleToggle}
                page={page}
                pageSize={pageSize}
                onPageChange={(nextPage, nextSize) => {
                  setPage(nextPage);
                  setPageSize(nextSize);
                }}
                onSearchChange={(next) => {
                  setText(next);
                  setPage(1);
                }}
              />
            </div>

            <div className={styles.mapPane}>
              {/* Nothing checked means there is no bore to designate the ends of, so the
                  panel is absent rather than present-and-empty and the map takes the whole
                  rail — the sheet opens on the one thing there is to do, which is pick. */}
              {selected.length > 0 && (
                <WellboreEndDesignation
                  selected={selected}
                  surface={surface}
                  bottom={bottom}
                  onDesignate={handleDesignate}
                  pickRole={pickRole}
                  onArm={setPickRole}
                  preview={
                    awaitingOptions && inputRefusal === null
                      ? { ...preview, loading: true }
                      : preview
                  }
                  bottomReset={bottomReset && selected.length > 1}
                  validBottomEnds={validBottomEnds}
                  refusal={inputRefusal ? REFUSAL_MESSAGES[inputRefusal] : null}
                  disabled={builder.saving}
                />
              )}
              <LtdSelectionMap
                candidates={mapCandidates}
                framingCandidates={framingCandidates}
                selected={selected}
                trajectory={preview.geometry}
                orderedMembers={preview.orderedMembers}
                surface={surface}
                bottom={bottom}
                validBottomEnds={validBottomEnds}
                pickRole={pickRole}
                onPick={(role, ref) => {
                  handleDesignate(role, ref);
                  // The click that uses the armed role disarms it.
                  setPickRole(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Tearsheet>
  );
}
